import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api';
import { useApp } from '../context/AppContext';
import { t } from '../i18n/translations';

const s = {
  page: { maxWidth: 920, margin: '0 auto', padding: '32px 16px' },
  back: { color: 'var(--primary)', cursor: 'pointer', marginBottom: 20, fontSize: 15, fontWeight: 600 },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--heading)' },
  topBtns: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  sosBtn: { background: '#ef4444', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', letterSpacing: 1 },
  pdfBtn: { background: '#7c3aed', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  emailBtn: { background: '#0891b2', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  tabs: { display: 'flex', gap: 4, marginBottom: 28, background: 'var(--surface2)', borderRadius: 12, padding: 5 },
  tab: { flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, transition: 'all 0.15s' },
  // Medications
  card: { background: 'var(--surface)', borderRadius: 14, padding: 22, marginBottom: 16, boxShadow: 'var(--shadow)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  medName: { fontSize: 17, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 },
  medDetail: { color: 'var(--text3)', fontSize: 13 },
  times: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  timeBadge: { background: 'var(--primary-light)', color: 'var(--primary)', padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  cardBtns: { display: 'flex', gap: 8, flexShrink: 0, marginLeft: 12 },
  editBtn: { background: 'var(--surface2)', color: 'var(--text3)', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  delBtn: { background: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 },
  modalCard: { background: 'var(--surface)', padding: 36, borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: 20, fontWeight: 700, marginBottom: 22, color: 'var(--heading)' },
  label: { display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--text2)', fontSize: 14 },
  input: { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, marginBottom: 14, boxSizing: 'border-box', background: 'var(--surface2)', color: 'var(--text)' },
  timeRow: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  addTimeBtn: { background: 'var(--primary-light)', color: 'var(--primary)', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  cancelBtn: { flex: 1, padding: 12, border: '1.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 600 },
  saveBtn: { flex: 1, padding: 12, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  empty: { textAlign: 'center', padding: 50, color: 'var(--text4)' },
  logSection: { marginTop: 32 },
  logTitle: { fontSize: 18, fontWeight: 700, color: 'var(--heading)', marginBottom: 14 },
  logCard: { background: 'var(--surface)', borderRadius: 12, padding: 16, marginBottom: 10, boxShadow: 'var(--shadow)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  takenBadge: { background: 'var(--success-bg)', color: 'var(--success-text)', padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  takenBtn: { background: '#16a34a', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  // Vitals
  vitalTypeRow: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  vitalTypeBtn: { padding: '8px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  vitalCard: { background: 'var(--surface)', borderRadius: 14, padding: 18, marginBottom: 10, boxShadow: 'var(--shadow)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  vitalVal: { fontSize: 22, fontWeight: 800, color: 'var(--heading)' },
  vitalUnit: { fontSize: 13, color: 'var(--text3)', marginLeft: 4 },
  vitalDate: { color: 'var(--text4)', fontSize: 13 },
  chartWrap: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 20, boxShadow: 'var(--shadow)' },
  // Journal
  moodRow: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  moodBtn: { padding: '8px 16px', borderRadius: 20, border: '2px solid transparent', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  journalCard: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 12, boxShadow: 'var(--shadow)' },
  journalDate: { fontSize: 13, fontWeight: 700, color: 'var(--text3)', marginBottom: 8 },
  journalText: { color: 'var(--text2)', fontSize: 14, marginBottom: 6 },
  journalMeta: { color: 'var(--text4)', fontSize: 13 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--heading)' },
  sosModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  sosCard: { background: 'var(--surface)', padding: 40, borderRadius: 20, maxWidth: 400, width: '100%', textAlign: 'center' },
  // Assignments
  assignSection: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 24, boxShadow: 'var(--shadow)' },
  assignTitle: { fontSize: 16, fontWeight: 700, color: 'var(--heading)', marginBottom: 12 },
  assignRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  assignName: { flex: 1, fontSize: 14, color: 'var(--text2)', fontWeight: 600 },
  assignRemove: { background: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', padding: '5px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  assignSelect: { padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, background: 'var(--surface2)', color: 'var(--text)', marginRight: 8 },
  assignBtn: { background: 'var(--primary)', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
};

const MOODS = [
  { value: 'great', label: '😄 Great', color: '#16a34a', bg: '#dcfce7' },
  { value: 'good', label: '🙂 Good', color: '#2563eb', bg: '#dbeafe' },
  { value: 'okay', label: '😐 Okay', color: '#b45309', bg: '#fef9c3' },
  { value: 'poor', label: '😟 Poor', color: '#ea580c', bg: '#ffedd5' },
  { value: 'bad', label: '😢 Bad', color: '#dc2626', bg: '#fee2e2' }
];

const VITAL_TYPES = [
  { value: 'blood_pressure', label: '🩸 Blood Pressure', unit: 'mmHg', color: '#dc2626' },
  { value: 'heart_rate', label: '❤️ Heart Rate', unit: 'bpm', color: '#ef4444' },
  { value: 'weight', label: '⚖️ Weight', unit: 'kg', color: '#2563eb' },
  { value: 'blood_glucose', label: '🍬 Blood Glucose', unit: 'mg/dL', color: '#b45309' }
];

const emptyMedForm = { name: '', dosage: '', instructions: '', totalPills: '', pillsPerDose: '', refillAlertAt: '' };
const emptyVitalForm = { type: 'blood_pressure', value: '', systolic: '', diastolic: '', date: new Date().toISOString().split('T')[0], time: `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes()).padStart(2,'0')}`, notes: '' };
const emptyJournalForm = { mood: 'good', notes: '', activity: '', appetite: '', date: new Date().toISOString().split('T')[0] };

export default function SeniorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useApp();
  const T = (key) => t(language, key);
  const [activeTab, setActiveTab] = useState('medications');
  const [seniorName, setSeniorName] = useState('');
  const [seniorData, setSeniorData] = useState(null);
  // Assignments
  const [assignedCaregivers, setAssignedCaregivers] = useState([]);
  const [allCaregivers, setAllCaregivers] = useState([]);
  const [assignSelectId, setAssignSelectId] = useState('');
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  // Medications
  const [medications, setMedications] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showMedModal, setShowMedModal] = useState(false);
  const [editMed, setEditMed] = useState(null);
  const [medForm, setMedForm] = useState(emptyMedForm);
  const [times, setTimes] = useState(['08:00']);
  // Vitals
  const [vitals, setVitals] = useState([]);
  const [vitalType, setVitalType] = useState('blood_pressure');
  const [showVitalModal, setShowVitalModal] = useState(false);
  const [vitalForm, setVitalForm] = useState(emptyVitalForm);
  // Journal
  const [journal, setJournal] = useState([]);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [editJournal, setEditJournal] = useState(null);
  const [journalForm, setJournalForm] = useState(emptyJournalForm);
  // SOS
  const [showSOS, setShowSOS] = useState(false);
  const [sosResult, setSosResult] = useState(null);
  const [sosSending, setSosSending] = useState(false);
  // SMS Invite
  const [smsInviteSending, setSmsInviteSending] = useState(false);
  const [smsInviteMsg, setSmsInviteMsg] = useState(null);

  useEffect(() => { fetchAll(); }, [id]);

  const fetchAll = async () => {
    const [medsRes, seniorsRes, logsRes, vitalsRes, journalRes, caregiverRes, assignRes] = await Promise.allSettled([
      api.get(`/medications/senior/${id}`),
      api.get('/seniors'),
      api.get(`/medications/log/${id}`),
      api.get(`/vitals/senior/${id}`),
      api.get(`/journal/senior/${id}`),
      api.get('/caregivers'),
      api.get(`/assignments/senior/${id}`)
    ]);
    if (medsRes.status === 'fulfilled') setMedications(medsRes.value.data);
    if (logsRes.status === 'fulfilled') setLogs(logsRes.value.data);
    if (vitalsRes.status === 'fulfilled') setVitals(vitalsRes.value.data);
    if (journalRes.status === 'fulfilled') setJournal(journalRes.value.data);
    if (caregiverRes.status === 'fulfilled') setAllCaregivers(caregiverRes.value.data);
    if (assignRes.status === 'fulfilled') setAssignedCaregivers(assignRes.value.data);
    if (seniorsRes.status === 'fulfilled') {
      const senior = seniorsRes.value.data.find(s => s._id === id);
      if (senior) { setSeniorName(senior.name); setSeniorData(senior); }
    }
  };

  // ---- ASSIGNMENTS ----
  const assignCaregiver = async () => {
    if (!assignSelectId) return;
    try { await api.post('/assignments', { seniorId: id, caregiverId: assignSelectId }); setAssignSelectId(''); fetchAll(); } catch {}
  };
  const removeAssignment = async (caregiverId) => {
    await api.delete('/assignments', { data: { seniorId: id, caregiverId } });
    fetchAll();
  };

  // ---- EMAIL REPORT ----
  const sendEmailReport = async () => {
    if (!emailTo) return;
    setEmailSending(true); setEmailMsg(null);
    try {
      const doc = new jsPDF();
      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, 210, 32, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18); doc.setFont(undefined, 'bold');
      doc.text('SeniorCare — Health Report', 14, 14);
      doc.setFontSize(10); doc.setFont(undefined, 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 24);
      doc.setTextColor(30, 58, 138); doc.setFontSize(15); doc.setFont(undefined, 'bold');
      doc.text(seniorName, 14, 46);
      if (medications.length > 0) {
        doc.setFontSize(12); doc.text('Medications', 14, 58);
        autoTable(doc, { startY: 62, head: [['Medication', 'Dosage', 'Times']], body: medications.map(m => [m.name, m.dosage || '-', (Array.isArray(m.times) ? m.times : m.times.split(',')).join(', ')]), headStyles: { fillColor: [37, 99, 235] }, styles: { fontSize: 9 }, margin: { left: 14, right: 14 } });
      }
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      await api.post('/reports/email', { to: emailTo, subject: `Health Report — ${seniorName}`, pdfBase64, fileName: `${seniorName.replace(/\s+/g, '_')}_report.pdf` });
      setEmailMsg({ type: 'success', text: `Report sent to ${emailTo}!` });
    } catch (err) {
      setEmailMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send email' });
    } finally { setEmailSending(false); }
  };

  // ---- MEDICATIONS ----
  const openAddMed = () => { setEditMed(null); setMedForm(emptyMedForm); setTimes(['08:00']); setShowMedModal(true); };
  const openEditMed = (med) => {
    setEditMed(med); setMedForm({ name: med.name, dosage: med.dosage || '', instructions: med.instructions || '', totalPills: med.totalPills || '', pillsPerDose: med.pillsPerDose || '', refillAlertAt: med.refillAlertAt || '' });
    setTimes(Array.isArray(med.times) ? med.times : med.times.split(',').map(t => t.trim())); setShowMedModal(true);
  };
  const closeMedModal = () => { setShowMedModal(false); setEditMed(null); setMedForm(emptyMedForm); setTimes(['08:00']); };
  const submitMed = async (e) => {
    e.preventDefault();
    if (editMed) await api.put(`/medications/${editMed._id}`, { ...medForm, times });
    else await api.post('/medications', { ...medForm, senior_id: id, times });
    closeMedModal(); fetchAll();
  };
  const deleteMed = async (medId) => { if (!window.confirm('Remove this medication?')) return; await api.delete(`/medications/${medId}`); fetchAll(); };
  const markTaken = async (logId) => { await api.post(`/medications/log/${logId}/taken`); fetchAll(); };
  const addTime = () => setTimes([...times, '08:00']);
  const updateTime = (i, val) => { const t = [...times]; t[i] = val; setTimes(t); };
  const removeTime = (i) => setTimes(times.filter((_, idx) => idx !== i));

  // ---- VITALS ----
  const openAddVital = () => { setVitalForm({ ...emptyVitalForm, type: vitalType }); setShowVitalModal(true); };
  const closeVitalModal = () => { setShowVitalModal(false); setVitalForm(emptyVitalForm); };
  const submitVital = async (e) => {
    e.preventDefault();
    await api.post('/vitals', { ...vitalForm, seniorId: id });
    closeVitalModal(); fetchAll();
  };
  const deleteVital = async (vid) => { await api.delete(`/vitals/${vid}`); fetchAll(); };

  const filteredVitals = vitals.filter(v => v.type === vitalType);
  const chartData = filteredVitals.slice(-20).map(v => ({
    date: v.date,
    value: v.value || null,
    systolic: v.systolic || null,
    diastolic: v.diastolic || null
  }));

  const formatVitalDisplay = (v) => {
    if (v.type === 'blood_pressure') return `${v.systolic}/${v.diastolic}`;
    return v.value;
  };

  // ---- JOURNAL ----
  const openAddJournal = () => { setEditJournal(null); setJournalForm(emptyJournalForm); setShowJournalModal(true); };
  const openEditJournal = (entry) => {
    setEditJournal(entry);
    setJournalForm({ mood: entry.mood, notes: entry.notes || '', activity: entry.activity || '', appetite: entry.appetite || '', date: entry.date });
    setShowJournalModal(true);
  };
  const closeJournalModal = () => { setShowJournalModal(false); setEditJournal(null); setJournalForm(emptyJournalForm); };
  const submitJournal = async (e) => {
    e.preventDefault();
    if (editJournal) await api.put(`/journal/${editJournal._id}`, journalForm);
    else await api.post('/journal', { ...journalForm, seniorId: id });
    closeJournalModal(); fetchAll();
  };
  const deleteJournal = async (jid) => { if (!window.confirm('Delete this journal entry?')) return; await api.delete(`/journal/${jid}`); fetchAll(); };

  // ---- SOS ----
  const triggerSOS = async () => {
    setSosSending(true);
    try {
      const res = await api.post(`/seniors/${id}/sos`);
      setSosResult({ success: true, notified: res.data.notified });
    } catch (err) {
      setSosResult({ success: false, error: err.response?.data?.error || 'SOS failed' });
    } finally { setSosSending(false); }
  };

  // ---- SMS INVITE ----
  const sendSmsInvite = async () => {
    setSmsInviteSending(true); setSmsInviteMsg(null);
    try {
      const res = await api.post(`/seniors/${id}/send-sms-invite`);
      setSmsInviteMsg({ type: 'success', text: res.data.message });
      fetchAll();
    } catch (err) {
      setSmsInviteMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send invite' });
    } finally { setSmsInviteSending(false); }
  };

  // ---- PDF ----
  const generatePDF = async () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Header
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, 210, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20); doc.setFont(undefined, 'bold');
    doc.text('SeniorCare — Health Report', 14, 14);
    doc.setFontSize(11); doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${today}`, 14, 24);

    // Senior Info
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(16); doc.setFont(undefined, 'bold');
    doc.text(seniorName, 14, 46);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10); doc.setFont(undefined, 'normal');
    let yPos = 54;
    if (seniorData?.date_of_birth) {
      const age = Math.floor((Date.now() - new Date(seniorData.date_of_birth).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
      doc.text(`Age: ${age} years`, 14, yPos); yPos += 7;
    }
    if (seniorData?.phone) { doc.text(`Phone: ${seniorData.phone}`, 14, yPos); yPos += 7; }
    yPos += 4;

    // Medications
    doc.setTextColor(30, 58, 138); doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Current Medications', 14, yPos); yPos += 4;
    if (medications.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Medication', 'Dosage', 'Reminder Times', 'Instructions']],
        body: medications.map(m => [m.name, m.dosage || '-', (Array.isArray(m.times) ? m.times : m.times.split(',')).join(', '), m.instructions || '-']),
        headStyles: { fillColor: [37, 99, 235] },
        styles: { fontSize: 9 }, margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    } else { doc.setTextColor(148, 163, 184); doc.setFontSize(10); doc.text('No medications recorded.', 14, yPos + 4); yPos += 14; }

    // Recent Vitals
    doc.setTextColor(30, 58, 138); doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Recent Vitals (last 10)', 14, yPos); yPos += 4;
    const recentVitals = vitals.slice(0, 10);
    if (recentVitals.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Type', 'Value', 'Date', 'Notes']],
        body: recentVitals.map(v => [
          VITAL_TYPES.find(t => t.value === v.type)?.label.replace(/[^\w\s]/g, '').trim() || v.type,
          v.type === 'blood_pressure' ? `${v.systolic}/${v.diastolic} mmHg` : `${v.value} ${v.unit || ''}`,
          v.date, v.notes || '-'
        ]),
        headStyles: { fillColor: [124, 58, 237] },
        styles: { fontSize: 9 }, margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    } else { doc.setTextColor(148, 163, 184); doc.setFontSize(10); doc.text('No vitals recorded.', 14, yPos + 4); yPos += 14; }

    // Upcoming Appointments
    const apptRes = await api.get('/appointments');
    const myAppts = apptRes.data.filter(a => a.personId === id && a.status === 'upcoming').slice(0, 10);
    doc.setTextColor(30, 58, 138); doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text('Upcoming Appointments', 14, yPos); yPos += 4;
    if (myAppts.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Title', 'Type', 'Date', 'Time', 'Location']],
        body: myAppts.map(a => [a.title, a.type.replace('_', ' '), a.date, a.time, a.location || '-']),
        headStyles: { fillColor: [16, 163, 74] },
        styles: { fontSize: 9 }, margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    } else { doc.setTextColor(148, 163, 184); doc.setFontSize(10); doc.text('No upcoming appointments.', 14, yPos + 4); }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setTextColor(148, 163, 184); doc.setFontSize(9);
      doc.text(`SeniorCare Health Report — ${seniorName} — Page ${i} of ${pageCount}`, 14, 290);
    }

    doc.save(`${seniorName.replace(/\s+/g, '_')}_health_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const currentVitalType = VITAL_TYPES.find(t => t.value === vitalType);

  return (
    <div style={s.page} className="page-wrap">
      <div style={s.back} onClick={() => navigate('/')}>← Back to Dashboard</div>

      <div style={s.topBar}>
        <div style={s.title}>{seniorName}</div>
        <div style={s.topBtns} className="btn-group">
          <button style={s.pdfBtn} onClick={generatePDF}>📄 {T('pdfReport')}</button>
          <button style={s.emailBtn} onClick={() => { setShowEmailModal(true); setEmailMsg(null); }}>✉️ {T('emailReport')}</button>
          <button style={s.sosBtn} onClick={() => { setShowSOS(true); setSosResult(null); }}>🚨 SOS</button>
        </div>
      </div>

      {/* SMS Opt-In Status */}
      {seniorData?.phone && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '14px 20px', marginBottom: 20, boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>📱</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--heading)', fontSize: 14 }}>SMS Reminders</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                {seniorData.smsOptIn === true
                  ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Opted in — reminders active</span>
                  : seniorData.smsOptIn === false
                    ? <span style={{ color: '#dc2626', fontWeight: 600 }}>Opted out</span>
                    : <span style={{ color: '#b45309', fontWeight: 600 }}>Not enrolled — invite not sent yet</span>}
              </div>
            </div>
          </div>
          {seniorData.smsOptIn !== true && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {smsInviteMsg && (
                <span style={{ fontSize: 13, color: smsInviteMsg.type === 'success' ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {smsInviteMsg.text}
                </span>
              )}
              <button
                style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: smsInviteSending ? 'not-allowed' : 'pointer', opacity: smsInviteSending ? 0.7 : 1 }}
                onClick={sendSmsInvite}
                disabled={smsInviteSending}
              >
                {smsInviteSending ? 'Sending...' : '📨 Send SMS Invite'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Assigned Caregivers */}
      <div style={s.assignSection} className="surface">
        <div style={s.assignTitle}>👥 {T('assignedCaregivers')}</div>
        {assignedCaregivers.length === 0 ? (
          <div style={{ color: 'var(--text4)', fontSize: 14, marginBottom: 12 }}>{T('noAssignments')}</div>
        ) : (
          assignedCaregivers.map(cg => (
            <div key={cg._id} style={s.assignRow}>
              <span style={s.assignName}>👩‍⚕️ {cg.name} {cg.relationship ? `(${cg.relationship})` : ''}</span>
              <button style={s.assignRemove} onClick={() => removeAssignment(cg._id)}>{T('removeAssignment')}</button>
            </div>
          ))
        )}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
          <select style={s.assignSelect} value={assignSelectId} onChange={e => setAssignSelectId(e.target.value)}>
            <option value="">-- {T('assignCaregiver')} --</option>
            {allCaregivers.filter(c => !assignedCaregivers.find(a => a._id === c._id)).map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <button style={s.assignBtn} onClick={assignCaregiver}>{T('assignCaregiver')}</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs} className="tab-bar">
        {[['medications',`💊 ${T('medications')}`],['vitals',`📊 ${T('vitals')}`],['journal',`📝 ${T('journal')}`]].map(([key, label]) => (
          <button key={key} style={{ ...s.tab, background: activeTab === key ? 'var(--surface)' : 'transparent', color: activeTab === key ? 'var(--heading)' : 'var(--text3)', boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}
            onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {/* ===== MEDICATIONS TAB ===== */}
      {activeTab === 'medications' && (
        <>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Medications</div>
            <button style={s.btn} onClick={openAddMed}>+ Add Medication</button>
          </div>
          {medications.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 44, marginBottom: 12 }}>💊</div><div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No medications yet</div><div>Add a medication to start sending reminders</div></div>
          ) : medications.map(med => (
            <div key={med._id} style={s.card}>
              <div style={s.cardTop}>
                <div>
                  <div style={s.medName}>{med.name}</div>
                  {med.dosage && <div style={s.medDetail}>Dosage: {med.dosage}</div>}
                  {med.instructions && <div style={s.medDetail}>{med.instructions}</div>}
                  <div style={s.times}>
                    {(Array.isArray(med.times) ? med.times : med.times.split(',')).map((t, i) => (
                      <span key={i} style={s.timeBadge}>{t.trim()}</span>
                    ))}
                    {med.remainingPills != null && (
                      <span style={{ background: med.remainingPills <= (med.refillAlertAt || Math.ceil((med.totalPills||0)*0.2)) ? '#fee2e2' : '#f0fdf4', color: med.remainingPills <= (med.refillAlertAt || Math.ceil((med.totalPills||0)*0.2)) ? '#dc2626' : '#16a34a', padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                        {med.remainingPills} pills left
                      </span>
                    )}
                  </div>
                </div>
                <div style={s.cardBtns}>
                  <button style={s.editBtn} onClick={() => openEditMed(med)}>Edit</button>
                  <button style={s.delBtn} onClick={() => deleteMed(med._id)}>Remove</button>
                </div>
              </div>
            </div>
          ))}

          {logs.length > 0 && (
            <div style={s.logSection}>
              <div style={s.logTitle}>Today's Log</div>
              {logs.map(log => (
                <div key={log._id} style={s.logCard}>
                  <div>
                    <strong>{log.med_name}</strong>
                    {log.dosage && <span style={{ color: '#64748b', marginLeft: 8, fontSize: 13 }}>{log.dosage}</span>}
                    <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>Scheduled: {log.scheduledTime}</div>
                  </div>
                  <div>
                    {log.status === 'taken'
                      ? <span style={s.takenBadge}>Taken ✓</span>
                      : <button style={s.takenBtn} onClick={() => markTaken(log._id)}>Mark Taken</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== VITALS TAB ===== */}
      {activeTab === 'vitals' && (
        <>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Health Vitals</div>
            <button style={s.btn} onClick={openAddVital}>+ Log Vital</button>
          </div>

          <div style={s.vitalTypeRow}>
            {VITAL_TYPES.map(t => (
              <button key={t.value} style={{ ...s.vitalTypeBtn, background: vitalType === t.value ? t.color : '#f1f5f9', color: vitalType === t.value ? '#fff' : '#475569' }}
                onClick={() => setVitalType(t.value)}>{t.label}</button>
            ))}
          </div>

          {filteredVitals.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 44, marginBottom: 12 }}>📊</div><div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No {currentVitalType?.label} readings yet</div><div>Click "+ Log Vital" to add the first reading</div></div>
          ) : (
            <>
              {/* Chart */}
              {chartData.length > 1 && (
                <div style={s.chartWrap}>
                  <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 14 }}>{currentVitalType?.label} — Last {chartData.length} readings</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      {vitalType === 'blood_pressure' ? (
                        <>
                          <Line type="monotone" dataKey="systolic" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} name="Systolic" />
                          <Line type="monotone" dataKey="diastolic" stroke="#f87171" strokeWidth={2} dot={{ r: 4 }} name="Diastolic" />
                        </>
                      ) : (
                        <Line type="monotone" dataKey="value" stroke={currentVitalType?.color} strokeWidth={2} dot={{ r: 4 }} name={currentVitalType?.label} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Readings list */}
              {filteredVitals.map(v => (
                <div key={v._id} style={s.vitalCard}>
                  <div>
                    <span style={s.vitalVal}>{formatVitalDisplay(v)}</span>
                    <span style={s.vitalUnit}>{v.type === 'blood_pressure' ? 'mmHg' : v.unit}</span>
                    {v.notes && <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{v.notes}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={s.vitalDate}>{v.date} {v.time}</div>
                    <button style={{ ...s.delBtn, marginTop: 8, padding: '5px 10px', fontSize: 12 }} onClick={() => deleteVital(v._id)}>Delete</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {/* ===== JOURNAL TAB ===== */}
      {activeTab === 'journal' && (
        <>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Daily Care Journal</div>
            <button style={s.btn} onClick={openAddJournal}>+ Add Entry</button>
          </div>

          {journal.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 44, marginBottom: 12 }}>📝</div><div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No journal entries yet</div><div>Record daily observations about mood, appetite, and activity</div></div>
          ) : journal.map(entry => {
            const mood = MOODS.find(m => m.value === entry.mood) || MOODS[1];
            return (
              <div key={entry._id} style={s.journalCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={s.journalDate}>{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <span style={{ background: mood.bg, color: mood.color, padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>{mood.label}</span>
                    {entry.notes && <div style={{ ...s.journalText, marginTop: 10 }}>{entry.notes}</div>}
                    <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                      {entry.activity && <div style={s.journalMeta}>🏃 Activity: {entry.activity}</div>}
                      {entry.appetite && <div style={s.journalMeta}>🍽️ Appetite: {entry.appetite}</div>}
                    </div>
                  </div>
                  <div style={s.cardBtns}>
                    <button style={s.editBtn} onClick={() => openEditJournal(entry)}>Edit</button>
                    <button style={s.delBtn} onClick={() => deleteJournal(entry._id)}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ===== MEDICATION MODAL ===== */}
      {showMedModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>{editMed ? 'Edit Medication' : 'Add Medication'}</div>
            <form onSubmit={submitMed}>
              <label style={s.label}>Medication Name *</label>
              <input style={s.input} value={medForm.name} onChange={e => setMedForm({ ...medForm, name: e.target.value })} required placeholder="e.g. Metformin" />
              <label style={s.label}>Dosage</label>
              <input style={s.input} value={medForm.dosage} onChange={e => setMedForm({ ...medForm, dosage: e.target.value })} placeholder="e.g. 500mg" />
              <label style={s.label}>Reminder Times</label>
              {/* Quick-select presets */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {[['🌅 Morning', '08:00'], ['☀️ Noon', '12:00'], ['🌆 Evening', '18:00'], ['🌙 Night', '22:00']].map(([label, val]) => (
                  <button key={val} type="button"
                    style={{ background: times.includes(val) ? 'var(--primary)' : 'var(--surface2)', color: times.includes(val) ? '#fff' : 'var(--text3)', border: '1.5px solid var(--border)', padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => times.includes(val) ? setTimes(times.filter(t => t !== val)) : setTimes([...times, val])}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={s.timeRow}>
                {times.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="time" value={t} onChange={e => updateTime(i, e.target.value)} style={{ padding: '8px', borderRadius: 8, border: '1.5px solid #e2e8f0' }} />
                    {times.length > 1 && <button type="button" onClick={() => removeTime(i)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>×</button>}
                  </div>
                ))}
                <button type="button" style={s.addTimeBtn} onClick={addTime}>+ Add Time</button>
              </div>
              <label style={s.label}>Instructions</label>
              <input style={s.input} value={medForm.instructions} onChange={e => setMedForm({ ...medForm, instructions: e.target.value })} placeholder="e.g. Take with food" />
              <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0 16px', paddingTop: 14 }}>
                <div style={{ fontWeight: 700, color: 'var(--text2)', fontSize: 13, marginBottom: 10 }}>💊 Refill Tracking (optional)</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Total pills</label>
                    <input style={s.input} type="number" min="1" value={medForm.totalPills} onChange={e => setMedForm({ ...medForm, totalPills: e.target.value })} placeholder="e.g. 30" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Pills per dose</label>
                    <input style={s.input} type="number" min="1" value={medForm.pillsPerDose} onChange={e => setMedForm({ ...medForm, pillsPerDose: e.target.value })} placeholder="e.g. 1" />
                  </div>
                </div>
                <label style={s.label}>Alert when remaining ≤</label>
                <input style={s.input} type="number" min="1" value={medForm.refillAlertAt} onChange={e => setMedForm({ ...medForm, refillAlertAt: e.target.value })} placeholder="e.g. 7 (leave blank for 20%)" />
              </div>
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeMedModal}>Cancel</button>
                <button type="submit" style={s.saveBtn}>{editMed ? 'Update' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== VITAL MODAL ===== */}
      {showVitalModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>Log Vital Reading</div>
            <form onSubmit={submitVital}>
              <label style={s.label}>Type</label>
              <select style={s.input} value={vitalForm.type} onChange={e => setVitalForm({ ...vitalForm, type: e.target.value })}>
                {VITAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {vitalForm.type === 'blood_pressure' ? (
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Systolic (upper) *</label>
                    <input style={s.input} type="number" value={vitalForm.systolic} onChange={e => setVitalForm({ ...vitalForm, systolic: e.target.value })} required placeholder="e.g. 120" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Diastolic (lower) *</label>
                    <input style={s.input} type="number" value={vitalForm.diastolic} onChange={e => setVitalForm({ ...vitalForm, diastolic: e.target.value })} required placeholder="e.g. 80" />
                  </div>
                </div>
              ) : (
                <>
                  <label style={s.label}>Value * ({currentVitalType?.unit})</label>
                  <input style={s.input} type="number" step="0.1" value={vitalForm.value} onChange={e => setVitalForm({ ...vitalForm, value: e.target.value })} required placeholder={`Enter ${currentVitalType?.label}`} />
                </>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Date *</label>
                  <input style={s.input} type="date" value={vitalForm.date} onChange={e => setVitalForm({ ...vitalForm, date: e.target.value })} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Time</label>
                  <input style={s.input} type="time" value={vitalForm.time} onChange={e => setVitalForm({ ...vitalForm, time: e.target.value })} />
                </div>
              </div>
              <label style={s.label}>Notes</label>
              <input style={s.input} value={vitalForm.notes} onChange={e => setVitalForm({ ...vitalForm, notes: e.target.value })} placeholder="e.g. After morning walk" />
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeVitalModal}>Cancel</button>
                <button type="submit" style={s.saveBtn}>Save Reading</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== JOURNAL MODAL ===== */}
      {showJournalModal && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>{editJournal ? 'Edit Journal Entry' : 'Add Journal Entry'}</div>
            <form onSubmit={submitJournal}>
              <label style={s.label}>Date *</label>
              <input style={s.input} type="date" value={journalForm.date} onChange={e => setJournalForm({ ...journalForm, date: e.target.value })} required />
              <label style={s.label}>Mood</label>
              <div style={s.moodRow}>
                {MOODS.map(m => (
                  <button type="button" key={m.value}
                    style={{ ...s.moodBtn, background: journalForm.mood === m.value ? m.bg : '#f8fafc', color: journalForm.mood === m.value ? m.color : '#64748b', borderColor: journalForm.mood === m.value ? m.color : 'transparent' }}
                    onClick={() => setJournalForm({ ...journalForm, mood: m.value })}>{m.label}</button>
                ))}
              </div>
              <label style={s.label}>Notes / Observations</label>
              <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={journalForm.notes} onChange={e => setJournalForm({ ...journalForm, notes: e.target.value })} placeholder="How was the senior today? Any concerns?" />
              <label style={s.label}>Physical Activity</label>
              <input style={s.input} value={journalForm.activity} onChange={e => setJournalForm({ ...journalForm, activity: e.target.value })} placeholder="e.g. Short walk, gentle exercises" />
              <label style={s.label}>Appetite</label>
              <select style={s.input} value={journalForm.appetite} onChange={e => setJournalForm({ ...journalForm, appetite: e.target.value })}>
                <option value="">Select...</option>
                <option>Excellent</option>
                <option>Good</option>
                <option>Fair</option>
                <option>Poor</option>
                <option>Refused to eat</option>
              </select>
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeJournalModal}>Cancel</button>
                <button type="submit" style={s.saveBtn}>{editJournal ? 'Update' : 'Save Entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== EMAIL MODAL ===== */}
      {showEmailModal && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, maxWidth: 420 }} className="modal-inner">
            <div style={s.modalTitle}>✉️ {T('emailReport')}</div>
            <div style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 14 }}>
              Send a PDF health report for <strong>{seniorName}</strong> to any email address.
            </div>
            {emailMsg && (
              <div style={{ background: emailMsg.type === 'success' ? 'var(--success-bg)' : 'var(--error-bg)', color: emailMsg.type === 'success' ? 'var(--success-text)' : 'var(--error-text)', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontWeight: 600 }}>
                {emailMsg.text}
              </div>
            )}
            <label style={s.label}>Send to Email *</label>
            <input style={s.input} type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="recipient@email.com" required />
            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setShowEmailModal(false)}>{T('cancel')}</button>
              <button style={{ ...s.saveBtn, background: '#0891b2', opacity: emailSending ? 0.7 : 1 }}
                onClick={sendEmailReport} disabled={emailSending || !emailTo}>
                {emailSending ? 'Sending...' : '✉️ Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SOS MODAL ===== */}
      {showSOS && (
        <div style={s.sosModal}>
          <div style={s.sosCard}>
            {!sosResult ? (
              <>
                <div style={{ fontSize: 52, marginBottom: 12 }}>🚨</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626', marginBottom: 8 }}>Emergency SOS</div>
                <div style={{ color: '#64748b', marginBottom: 24, fontSize: 15 }}>This will send an emergency SMS to all caregivers linked to <strong>{seniorName}</strong>.</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button style={{ ...s.cancelBtn, flex: 1, padding: '13px 0' }} onClick={() => setShowSOS(false)}>Cancel</button>
                  <button style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none', padding: '13px 0', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: sosSending ? 'not-allowed' : 'pointer', opacity: sosSending ? 0.7 : 1 }}
                    onClick={triggerSOS} disabled={sosSending}>
                    {sosSending ? 'Sending...' : '🚨 Send SOS'}
                  </button>
                </div>
              </>
            ) : sosResult.success ? (
              <>
                <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#16a34a', marginBottom: 8 }}>SOS Sent!</div>
                <div style={{ color: '#64748b', marginBottom: 24 }}>{sosResult.notified} caregiver(s) have been notified.</div>
                <button style={{ ...s.btn, width: '100%', padding: 14 }} onClick={() => setShowSOS(false)}>Close</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 52, marginBottom: 12 }}>⚠️</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>SOS Error</div>
                <div style={{ color: '#64748b', marginBottom: 24 }}>{sosResult.error}</div>
                <button style={{ ...s.btn, width: '100%', padding: 14 }} onClick={() => setShowSOS(false)}>Close</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
